import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getDocuments, runPidQa, getPidTrackers, clearDocumentsCache } from "../lib/api";
import { MSOStatusProvider, MSOStatusCell } from "../components/mso/MSOStatusCell";
import { TrackScore, ScoreRing, scoreColor, IdrCell, FilterDropdown, Checkbox, Chip } from "../components/SharedComponents";
import { useRoles } from "../lib/useRoles";
import { useTechIdentity } from "../lib/TechIdentityContext";
import { techDisplayName } from "../lib/names";

// Tech name cell. Cases with a second tech get a "+T2" marker, since NP Reports
// can leave them out of Tech 1's grade.
function TechCell({ doc }) {
  const name = techDisplayName(doc?.techName) || "—";
  const tech2 = techDisplayName(doc?.tech2Name);
  if (!tech2) return name;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
      {name}
      <span title={`Tech 2: ${tech2}`} style={{ fontSize:9.5, fontWeight:700, padding:"0 4px", borderRadius:3,
        background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a", whiteSpace:"nowrap" }}>+T2</span>
    </span>
  );
}

/*
 ═══════════════════════════════════════════════════════════════
 4-STATE MODEL
   Open    = has failures, needs reviewer attention
   Partial = one audit side signed off, other still open
   Passed  = all checks passed, no action needed
   Closed  = both sides signed off by reviewer(s)
 ═══════════════════════════════════════════════════════════════
*/
function deriveStatus(doc, tracker, fileCount) {
  const rs = doc.reviewerStatus || "";
  const ts = tracker?.status || "";

  // Aborted outranks everything, including the file-count rule below: a
  // cancelled procedure is not IDR work either, it is simply not work.
  if (rs === "ABORTED") return "Aborted";

  // A case with one source file has nothing to cross-check against, so it is
  // IDR work rather than QA work. Decided by file count, not by the tracker:
  // these cases often ran QA on their single file and sit at QA_COMPLETE, so
  // a tracker-based rule missed them entirely.
  if (fileCount === 1) return "IDR only";

  // Human states outrank pipeline states: once someone has signed something
  // off, that is the more useful thing to say about the case.
  if (rs === "CLOSED") return "Closed";
  if (rs === "REVIEWED") return "Reviewed";
  if (rs === "PARTIALLY_REVIEWED") return "Partial";

  // Pipeline states, from the PID tracker.
  if (ts === "QA_FAILED") return "Failed";
  if (ts === "QA_RUNNING") return "Running";
  if (ts === "QUEUED") return "Queued";
  // READY means every source has registered and QA has been triggered, not
  // that QA has finished. Without this it fell through to "Ready for review"
  // below and claimed "QA complete" for a case whose QA had never run.
  if (ts === "READY") return "Queued";

  if (ts === "WAITING") return "Waiting for files";

  // QA has run and nobody has signed anything off yet. A clean case still
  // lands here: passing every check does not release it, a human does.
  return "Ready for review";
}

// Keys stay descriptive so the code reads plainly; `label` is what a chip
// shows and `title` carries the full meaning on hover, so a row of chips stays
// scannable without losing what each one means.
const STATUS_STYLE = {
  "IDR only":          { label:"IDR",      title:"Single-file case — IDR work, not QA",
                         dot:"#94a3b8", bg:"#f8fafc", text:"#475569", border:"#e2e8f0" },
  "Waiting for files": { label:"Waiting",  title:"Waiting for the remaining source files",
                         dot:"#64748b", bg:"#f8fafc", text:"#475569", border:"#e2e8f0" },
  Queued:              { label:"Queued",   title:"Queued for QA, not started yet",
                         dot:"#0891b2", bg:"#ecfeff", text:"#0891b2", border:"#a5f3fc" },
  Running:             { label:"Running",  title:"QA in progress",
                         dot:"#3b82f6", bg:"#eff6ff", text:"#1d4ed8", border:"#bfdbfe" },
  Failed:              { label:"Failed",   title:"QA did not complete — see the QA column for the reason",
                         dot:"#ef4444", bg:"#fef2f2", text:"#dc2626", border:"#fecaca" },
  "Ready for review":  { label:"Ready",    title:"QA complete, awaiting a human review",
                         dot:"#f59e0b", bg:"#fffbeb", text:"#b45309", border:"#fde68a" },
  Partial:             { label:"Partial",  title:"One audit side signed off, the other outstanding",
                         dot:"#d97706", bg:"#fffbeb", text:"#92400e", border:"#fcd34d" },
  Reviewed:            { label:"Reviewed", title:"Both document audits signed off",
                         dot:"#22c55e", bg:"#f0fdf4", text:"#16a34a", border:"#bbf7d0" },
  Closed:              { label:"Closed",   title:"Closed — reviewer confirmed no manual waveform portion",
                         dot:"#6366f1", bg:"#eef2ff", text:"#4f46e5", border:"#c7d2fe" },
  Aborted:             { label:"Aborted",  title:"Cancelled or abandoned case — excluded from QA and from all scores",
                         dot:"#9ca3af", bg:"#f9fafb", text:"#6b7280", border:"#e5e7eb" },
};

// Order for the filter pills and the counts behind them.
const STATUS_ORDER = ["Failed", "Waiting for files", "Queued", "Running",
                      "Ready for review", "Partial", "Reviewed", "Closed",
                      "IDR only", "Aborted"];

// Statuses that are real work but not QA work. They stay out of the default
// view and appear only when their own pill is selected.
const HIDDEN_BY_DEFAULT = ["IDR only", "Aborted"];
// The only statuses a tech can see (they have no Status filter).
const TECH_VISIBLE_STATUSES = ["Reviewed", "Closed"];
// An interpreting physician sees finished cases only. Closed means both audit
// sides are signed off, so there is nothing still moving for them to read a
// half-finished judgement from. Waiting, Ready and IDR only are work in
// progress or not QA work at all.
const IP_VISIBLE_STATUSES = ["Closed"];

/* A technologist's open flags on a case. Advisory: it rides next to the alert
   bell rather than in the status column, because a flag does not change where
   the case stands, only that someone has asked for a second look. */
/* Three states, so "handled" does not read the same as "never raised":
   a dash for a case nobody flagged, amber with a count while someone still
   owes an answer, and a muted flag once every one has been resolved. The
   muted state is the same grey the resolved chip on a check row uses. */
function FlagIndicator({ count, total = 0 }) {
  if (!count && !total) return <span style={{ color:"var(--text-tertiary)", fontSize:11 }}>—</span>;

  const chip = {
    display:"inline-flex", alignItems:"center", gap:3, padding:"2px 7px", borderRadius:12,
    fontSize:10, fontWeight:600, fontFamily:"var(--font-mono)",
  };

  if (!count) {
    return (
      <span title={`${total} check${total === 1 ? "" : "s"} flagged, all resolved`}
        style={{ ...chip, background:"var(--gray-50, #f9fafb)", border:"1px solid var(--border)",
          color:"var(--text-tertiary)" }}>
        &#9873;
      </span>
    );
  }

  // Borrows the Ready-for-review palette: an open flag is the same kind of
  // "someone still has to look at this" as Ready, not the red of a clinical alert.
  const c = STATUS_STYLE["Ready for review"];
  return (
    <span title={`${count} check${count === 1 ? "" : "s"} flagged by the technologist, awaiting a reviewer`}
      style={{ ...chip, background:c.bg, border:`1px solid ${c.border}`, color:c.text }}>
      &#9873;{count}
    </span>
  );
}

/* helper: is this doc a source-typed file (not Combined, not PID_QA)? */
const isSourceFile = d => d.sourceType && d.sourceType !== "Combined" && d.sourceType !== "PID_QA";

/* ── Tiny Components ──────────────────────────────────────────── */

const TRACK_OPTIONS = [
  { label: "Tech doc not run",    group: "tech", test: d => !(d?.techTotalRules > 0) },
  { label: "Tech doc done",       group: "tech", test: d => d?.techTotalRules > 0 && !d?.techReviewed },
  { label: "Tech doc reviewed",   group: "tech", test: d => !!d?.techReviewed },

  { label: "IP QA not run",       group: "ip",   test: d => !(d?.ipTotalRules > 0) },
  { label: "IP QA done",          group: "ip",   test: d => d?.ipTotalRules > 0 && !d?.ipReviewed },
  { label: "IP reviewed",         group: "ip",   test: d => !!d?.ipReviewed },

  { label: "Tech wave reviewed",  group: "wave", test: d => !!d?.waveformSigned },
  { label: "Tech wave not done",  group: "wave", test: d => !d?.waveformSigned },

  { label: "IDR not started", group: "idr", test: d => !d?.idrStatus && !d?.idrHasNarrative },
  { label: "IDR drafted",     group: "idr", test: d => !!d?.idrHasNarrative && d?.idrStatus !== "reviewed" },
  { label: "IDR reviewed",    group: "idr", test: d => d?.idrStatus === "reviewed" },
];

function matchesTracks(doc, trackF) {
  if (!trackF.length) return true;
  const chosen = TRACK_OPTIONS.filter(o => trackF.includes(o.label));
  const groups = [...new Set(chosen.map(o => o.group))];
  return groups.every(g => chosen.filter(o => o.group === g).some(o => o.test(doc)));
}

function StatusBadge({ status, title }) {
  const c = STATUS_STYLE[status] || STATUS_STYLE["Ready for review"];
  return (
    <span title={title || c.title} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px 3px 8px",
      borderRadius:20, background:c.bg, border:`1px solid ${c.border}`, fontSize:11, fontWeight:600, color:c.text }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:c.dot, flexShrink:0 }}/>{c.label}
    </span>
  );
}

function AlertIndicator({ status, count }) {
  const has = status === "ALERT" || status === "MANUAL_ALERT";
  if (!has) return <span style={{ color:"var(--text-tertiary)", fontSize:11 }}>—</span>;
  return (
    <span title={`${count} alert${count !== 1 ? "s" : ""}`} style={{
      display:"inline-flex", alignItems:"center", gap:3, padding:"2px 7px", borderRadius:12,
      background:"#fef2f2", border:"1px solid #fecaca", fontSize:10, fontWeight:600, color:"#dc2626", fontFamily:"var(--font-mono)" }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>{count}
    </span>
  );
}

const SOURCE_BADGE = {
  "Usmon Chat Log":         { bg:"#eff6ff", color:"#2563eb", border:"#bfdbfe", short:"CHAT" },
  "Interpretation Report":  { bg:"#f5f3ff", color:"#7c3aed", border:"#ddd6fe", short:"IP" },
  "Technical Report":       { bg:"#ecfeff", color:"#0891b2", border:"#a5f3fc", short:"TECH" },
  "Usmon Screenshots":      { bg:"#fef3c7", color:"#b45309", border:"#fde68a", short:"SCREENSHOT" },
  "Combined":               { bg:"#f8fafc", color:"#64748b", border:"#e2e8f0", short:"ALL" },
};

function SourceTypeBadge({ sourceType }) {
  if (!sourceType || sourceType === "Combined") return null;
  const s = SOURCE_BADGE[sourceType] || SOURCE_BADGE.Combined;
  return (
    <span style={{ fontSize:9, fontFamily:"var(--font-mono)", fontWeight:600, padding:"1px 5px", borderRadius:3,
      background:s.bg, color:s.color, border:`1px solid ${s.border}`, textTransform:"uppercase", letterSpacing:"0.03em" }}>
      {s.short}
    </span>
  );
}

/* ── Floating Selection Bar (no Close Selected) ───────────────── */
function SelectionBar({ count, onClear, onAudit }) {
  if (!count) return null;
  return (
    <div style={{ position:"fixed", bottom:20, left:"50%", transform:"translateX(-50%)", zIndex:900,
      display:"flex", alignItems:"center", gap:12, padding:"10px 18px",
      background:"var(--white)", border:"1px solid var(--border)", borderRadius:12,
      boxShadow:"0 8px 28px rgba(0,0,0,0.12)", fontFamily:"var(--font-mono)", fontSize:12,
      animation:"floatUp 0.2s ease" }}>
      <span style={{ fontWeight:600, color:"#1d4ed8" }}>{count} selected</span>
      <div style={{ width:1, height:18, background:"var(--border)" }}/>
      <button onClick={onAudit} style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:7,
        background:"#3b82f6", color:"#fff", border:"none", fontSize:11.5, fontWeight:600, fontFamily:"var(--font-mono)",
        cursor:"pointer", boxShadow:"0 2px 6px rgba(59,130,246,0.3)" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
          <rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/>
        </svg>Generate Report
      </button>
      <button onClick={onClear} style={{ padding:"5px 9px", borderRadius:7, background:"transparent",
        color:"var(--text-tertiary)", border:"1px solid var(--border)", fontSize:10.5, fontFamily:"var(--font-mono)", cursor:"pointer" }}>Clear</button>
    </div>
  );
}

/* ── Helper: counts and score for one audit track ──────────────────
   The score is whatever the backend computed in document_scores.py -
   points-weighted, and never pooling Technologist with Interpreting
   Physician. Nothing is derived from counts here; a track with no
   scorable points is null, which TrackScore renders as an em dash
   rather than a red 0%.                                            */
function getAuditScoreData(doc, auditType) {
  if (auditType === "Technologist") {
    return {
      pass:  doc.techPassCount  || 0,
      fail:  doc.techFailCount  || 0,
      total: doc.techTotalRules || 0,
      score: doc.techScore ?? null,
    };
  }
  if (auditType === "IP") {
    return {
      pass:  doc.ipPassCount  || 0,
      fail:  doc.ipFailCount  || 0,
      total: doc.ipTotalRules || 0,
      score: doc.ipScore ?? null,
    };
  }
  // There is deliberately no combined track. Ask for the one you mean.
  return { pass: 0, fail: 0, total: 0, score: null };
}

/* ═══════════════════════════════════════════════════════════════ */
/*  MAIN                                                          */
/* ═══════════════════════════════════════════════════════════════ */

export default function Documents() {
  const nav = useNavigate();
  const { isTech, isAdmin, isIP } = useRoles();
  const { effectiveWebId, effectiveReaderId, loading: identityLoading } = useTechIdentity();
  // Viewing as an IP is scoped by the server, so the reader has to travel
  // with the request. It also keys the client cache, so one physician's
  // list is never reused for another.
  const asReader = effectiveReaderId ? { readerId: effectiveReaderId } : {};
  // isTech/isAdmin are already the effective roles, so an admin viewing as a
  // tech scopes here the same way the tech does.
  const scopeToMe = isTech && !isAdmin;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pidTrackers, setPidTrackers] = useState({});

  // Filters
  const [search, setSearch]       = useState("");
  const [statF, setStatF]         = useState([]);
  const [alertF, setAlertF]       = useState([]);
  const [flagF, setFlagF]         = useState([]);
  const [waveF, setWaveF]         = useState([]);
  const [trackF, setTrackF]       = useState([]);
  const [procF, setProcF]         = useState([]);
  const [facF, setFacF]           = useState([]);
  const [surgF, setSurgF]         = useState([]);
  const [techF, setTechF]         = useState([]);
  const [revF, setRevF]           = useState([]);
  const [dateFrom, setDateFrom]   = useState("");
  const [dateTo, setDateTo]       = useState("");
  const [quarter, setQuarter]     = useState("");
  const [sortBy, setSortBy]       = useState("serviceDate");
  const [sortDir, setSortDir]     = useState("desc");
  const [sel, setSel]             = useState(new Set());

  useEffect(() => {
    setLoading(true);
    Promise.all([getDocuments({ ...asReader }), getPidTrackers()])
      .then(([docList, trackerList]) => {
        setDocs(docList);
        const tMap = {};
        for (const t of trackerList) tMap[t.id || t.patient_id] = t;
        setPidTrackers(tMap);
      })
      .finally(() => setLoading(false));
  }, []);

  // Live-refresh while any case is mid-QA so the "QA Running" badge clears itself.
  useEffect(() => {
    const anyRunning = Object.values(pidTrackers).some(t => t?.status === "QA_RUNNING");
    if (!anyRunning) return;
    const iv = setInterval(() => {
      Promise.all([getDocuments({ ...asReader }), getPidTrackers()]).then(([docList, trackerList]) => {
        setDocs(docList);
        const tMap = {};
        for (const t of trackerList) tMap[t.id || t.patient_id] = t;
        setPidTrackers(tMap);
      }).catch(() => {});
    }, 8000);
    return () => clearInterval(iv);
  }, [pidTrackers]);

  const enriched = useMemo(() => docs.map(d => {
    const p = (d.procedure_name || d.procedureType || "").trim();
    return { ...d, _st: deriveStatus(d, pidTrackers[d.patientId]),
             procedureType: !p || p.toUpperCase() === "UNKNOWN" ? "Unknown" : p };
  }), [docs, pidTrackers]);

  /* ── Build PID groups from ALL docs (unfiltered) ──────────── */
  const allPidGroups = useMemo(() => {
    const groups = [];
    const seen = new Map();
    for (const doc of enriched) {
      if (doc.sourceType === "PID_QA") continue;
      const pid = doc.patientId || doc.id;
      if (seen.has(pid)) {
        groups[seen.get(pid)].docs.push(doc);
      } else {
        seen.set(pid, groups.length);
        groups.push({ pid, patientName: doc.patientName || "", docs: [doc] });
      }
    }
    const pidQaDocs = enriched.filter(d => d.sourceType === "PID_QA");
    for (const pqDoc of pidQaDocs) {
      const pid = pqDoc.patientId;
      if (pid && seen.has(pid)) {
        groups[seen.get(pid)].pidQaDoc = pqDoc;
      }
    }
    if (isIP) {
      // Mirrored server side in list_documents; this is what keeps the counts
      // and pills honest rather than what keeps the data safe.
      return groups.filter(g => {
        const r = g.pidQaDoc || g.docs[0];
        return IP_VISIBLE_STATUSES.includes(deriveStatus(r, pidTrackers[g.pid], g.docs.length));
      });
    }
    if (!isTech) return groups;
    // Techs only see cases a reviewer has finished: Reviewed or Closed.
    let out = groups.filter(g => {
      const r = g.pidQaDoc || g.docs[0];
      return r?.techReviewed === true &&
        TECH_VISIBLE_STATUSES.includes(deriveStatus(r, pidTrackers[g.pid], g.docs.length));
    });
    if (scopeToMe) {
      out = effectiveWebId
        ? out.filter(g => String((g.pidQaDoc || g.docs[0])?.techWebId || "") === String(effectiveWebId))
        : [];
    }
    return out;
  }, [enriched, isTech, isIP, scopeToMe, effectiveWebId, pidTrackers]);

  /* ── Case-level counts (each PID group = 1 case) ─────────── */
  const caseCounts = useMemo(() => {
    const c = { total: 0, alerts: 0, flagged: 0, waveform: 0 };
    for (const s of STATUS_ORDER) c[s] = 0;
    for (const g of allPidGroups) {
      c.total++;
      const rep = g.pidQaDoc || g.docs[0];
      const st = deriveStatus(rep, pidTrackers[g.pid], g.docs.length);
      c[st] = (c[st] || 0) + 1;
      const alertSt = rep.alertStatus || "";
      if (alertSt === "ALERT" || alertSt === "MANUAL_ALERT") c.alerts++;
      if ((rep.techFlagOpen || 0) > 0) c.flagged++;
      if (rep.waveformSelected) c.waveform++;
    }
    return c;
  }, [allPidGroups]);

  /* ── Filter + sort at GROUP level ─────────────────────────── */
  const filteredGroups = useMemo(() => {
    let groups = allPidGroups;

    // Get the "representative" for a group: PID_QA doc if multi-file, else the doc itself
    const rep = g => g.pidQaDoc || g.docs[0];

    if (search) {
      const q = search.toLowerCase();
      groups = groups.filter(g => {
        const r = rep(g);
        return g.docs.some(d => d.filename.toLowerCase().includes(q)) ||
          g.pid.toLowerCase().includes(q) ||
          (r.procedureType || "").toLowerCase().includes(q) ||
          (r.surgeonName || "").toLowerCase().includes(q) ||
          (r.technicianName || "").toLowerCase().includes(q) ||
          (r.facility || "").toLowerCase().includes(q);
      });
    }

    groups = groups.filter(g => {
      const st = deriveStatus(rep(g), pidTrackers[g.pid], g.docs.length);
      if (statF.length) return statF.includes(st);
      return !HIDDEN_BY_DEFAULT.includes(st);
    });

    if (waveF.includes("SELECTED")) {
      groups = groups.filter(g => !!rep(g)?.waveformSelected);
    }

    if (flagF.includes("FLAGGED")) {
      groups = groups.filter(g => (rep(g)?.techFlagOpen || 0) > 0);
    }

    if (alertF.includes("ALERTS")) {
      groups = groups.filter(g => {
        const r = rep(g);
        return r.alertStatus === "ALERT" || r.alertStatus === "MANUAL_ALERT" ||
          g.docs.some(d => d.alertStatus === "ALERT" || d.alertStatus === "MANUAL_ALERT");
      });
    }

    if (procF.length) groups = groups.filter(g => procF.includes(rep(g).procedureType));
    if (facF.length)  groups = groups.filter(g => facF.includes(rep(g).facility));
    if (surgF.length) groups = groups.filter(g => surgF.includes(rep(g).surgeonName));
    if (techF.length) groups = groups.filter(g => techF.includes(techDisplayName(rep(g).techName)));
    if (revF.length) groups = groups.filter(g => revF.includes(rep(g).reviewedBy));
    if (trackF.length) groups = groups.filter(g => matchesTracks(rep(g), trackF));

    if (dateFrom) {
      groups = groups.filter(g => {
        const d = rep(g).serviceDate;
        return d && d >= dateFrom;
      });
    }
    if (dateTo) {
      groups = groups.filter(g => {
        const d = rep(g).serviceDate;
        return d && d <= dateTo;
      });
    }

    // Sort
    groups = [...groups].sort((a, b) => {
      const ar = rep(a), br = rep(b);
      let av, bv;
      if (sortBy === "qaScore") {
        av = getAuditScoreData(ar, "Technologist").score;
        bv = getAuditScoreData(br, "Technologist").score;
      } else if (sortBy === "uploadedAt") {
        // For groups, use latest upload date among children
        av = a.docs.reduce((lat, d) => (!lat || (d.uploadedAt && d.uploadedAt > lat)) ? d.uploadedAt : lat, "");
        bv = b.docs.reduce((lat, d) => (!lat || (d.uploadedAt && d.uploadedAt > lat)) ? d.uploadedAt : lat, "");
      } else if (sortBy === "techFlagOpen") {
        // A case with no flags sorts as zero, not as a missing value, so the
        // flagged ones gather at one end instead of scattering.
        av = ar.techFlagOpen || 0; bv = br.techFlagOpen || 0;
      } else {
        av = ar[sortBy]; bv = br[sortBy];
      }
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === "string") { av = av.toLowerCase(); bv = (bv||"").toLowerCase(); }
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

    return groups;
  }, [allPidGroups, search, statF, alertF, flagF, waveF, trackF, procF, facF, surgF, techF, revF, dateFrom, dateTo, sortBy, sortDir]);

  const [expandedPids, setExpandedPids] = useState(new Set());
  const togglePid = pid => setExpandedPids(p => { const s = new Set(p); s.has(pid) ? s.delete(pid) : s.add(pid); return s; });

  // PID QA trigger state
  const [pidQaRunning, setPidQaRunning] = useState(new Set());
  const handleRunPidQa = async (pid, force = false) => {
    setPidQaRunning(p => { const s = new Set(p); s.add(pid); return s; });
    try {
      await runPidQa(pid, "Unknown", force);
      clearDocumentsCache();
      const [fresh, trackerList] = await Promise.all([getDocuments({ ...asReader }), getPidTrackers()]);
      setDocs(fresh);
      const tMap = {};
      for (const t of trackerList) tMap[t.id || t.patient_id] = t;
      setPidTrackers(tMap);
    } catch (e) {
      alert("PID QA failed: " + (e?.message || String(e)));
    }
    setPidQaRunning(p => { const s = new Set(p); s.delete(pid); return s; });
  };

  // Unique filter options (from all groups, not filtered)
  const procs = useMemo(() => [...new Set(allPidGroups.map(g => (g.pidQaDoc || g.docs[0]).procedureType).filter(Boolean))].sort(), [allPidGroups]);
  const facs  = useMemo(() => [...new Set(allPidGroups.map(g => (g.pidQaDoc || g.docs[0]).facility).filter(v => v && v !== "Unknown"))].sort(), [allPidGroups]);
  const surgs = useMemo(() => [...new Set(allPidGroups.map(g => (g.pidQaDoc || g.docs[0]).surgeonName).filter(v => v && v !== "Unknown"))].sort(), [allPidGroups]);
  const techs = useMemo(() => [...new Set(allPidGroups.map(g => techDisplayName((g.pidQaDoc || g.docs[0]).techName)).filter(Boolean))].sort(), [allPidGroups]);
  const reviewers = useMemo(() => [...new Set(allPidGroups.map(g => (g.pidQaDoc || g.docs[0]).reviewedBy).filter(v => v && v !== "Unknown"))].sort(), [allPidGroups]);

  const quarterOpts = useMemo(() => {
    const yrs = [...new Set(allPidGroups
      .map(g => (g.pidQaDoc || g.docs[0]).serviceDate)
      .filter(Boolean).map(d => d.slice(0, 4)))].sort().reverse();
    const out = [];
    yrs.forEach(y => [1, 2, 3, 4].forEach(q => out.push(`${y}-Q${q}`)));
    return out;
  }, [allPidGroups]);

  const applyQuarter = (val) => {
    setQuarter(val);
    if (!val) { setDateFrom(""); setDateTo(""); return; }
    const yr = parseInt(val.slice(0, 4), 10);
    const qn = parseInt(val.slice(6), 10);
    const sm = (qn - 1) * 3;
    const pad = n => String(n).padStart(2, "0");
    const last = new Date(yr, sm + 3, 0).getDate();
    setDateFrom(`${yr}-${pad(sm + 1)}-01`);
    setDateTo(`${yr}-${pad(sm + 3)}-${pad(last)}`);
  };

  const toggleSort = c => { if (sortBy === c) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(c); setSortDir("desc"); } };
  const Arr = ({ c: col }) => <span style={{ marginLeft:2, opacity: sortBy === col ? 0.9 : 0.2, fontSize:9 }}>{sortBy === col ? (sortDir === "asc" ? "▲" : "▼") : "▲"}</span>;

  // Selection — for PID groups, select the PID_QA virtual doc ID (for accurate audit reports)
  const togSel = id => setSel(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const getGroupSelId = g => g.pidQaDoc ? g.pidQaDoc.id : g.docs[0]?.id;

  const allSel = filteredGroups.length > 0 && filteredGroups.every(g => {
    if (g.docs.length === 1) return sel.has(g.docs[0].id);
    return sel.has(getGroupSelId(g));
  });
  const someSel = filteredGroups.some(g => {
    if (g.docs.length === 1) return sel.has(g.docs[0].id);
    return sel.has(getGroupSelId(g));
  });
  const togAll = () => {
    setSel(p => {
      const s = new Set(p);
      if (allSel) {
        filteredGroups.forEach(g => {
          if (g.docs.length === 1) s.delete(g.docs[0].id);
          else s.delete(getGroupSelId(g));
        });
      } else {
        filteredGroups.forEach(g => {
          if (g.docs.length === 1) s.add(g.docs[0].id);
          else s.add(getGroupSelId(g));
        });
      }
      return s;
    });
  };

  const hasF = statF.length + alertF.length + flagF.length + waveF.length + trackF.length + procF.length + facF.length + surgF.length + techF.length + revF.length + (dateFrom?1:0) + (dateTo?1:0) > 0;
  const clearAll = () => { setStatF([]); setAlertF([]); setTrackF([]); setProcF([]); setFacF([]); setSurgF([]); setTechF([]); setRevF([]); setDateFrom(""); setDateTo(""); setQuarter(""); setSearch(""); };

  const fmtDate = d => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";
  const fmtSvc = s => { if (!s) return "—"; const p = s.split("-"); return p.length === 3 ? `${p[1]}/${p[2]}/${p[0].slice(2)}` : s; };

  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">Documents</div>
          <div className="page-subtitle" style={{ fontSize:12, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)" }}>
            {caseCounts.total} total · {caseCounts["Ready for review"] > 0 ? `${caseCounts["Ready for review"]} awaiting review` : "none awaiting review"}{caseCounts.Failed > 0 ? ` · ${caseCounts.Failed} failed` : ""}{caseCounts.alerts > 0 ? ` · ${caseCounts.alerts} with alerts` : ""}
          </div>
        </div>
        <div className="header-actions">
          {!isTech && (
            <button className="btn btn-primary btn-sm" onClick={() => nav("/upload")}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{width:14,height:14}}><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg>
              Upload
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Row 1: Search + status pills + alert toggle */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8, flexWrap:"wrap" }}>
          <div style={{ position:"relative", flex:1, maxWidth:300 }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5" style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)" }}>
              <circle cx="7" cy="7" r="5"/><path d="M12 12l3 3" strokeLinecap="round"/>
            </svg>
            <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ width:"100%", boxSizing:"border-box", padding:"7px 10px 7px 30px", fontSize:12, fontFamily:"var(--font-mono)",
                border:"1px solid var(--border)", borderRadius:7, outline:"none", background:"var(--white)", color:"var(--text-primary)" }}/>
          </div>
          <div style={{ display:"flex", gap:3, flexWrap:"wrap", rowGap:4, flex:"1 1 auto", minWidth:0 }}>
            {!isTech && (
              <>
                {[{k:null,l:"All",n:caseCounts.total,t:"Every case except IDR-only"}].concat(
                  STATUS_ORDER.filter(s => caseCounts[s] > 0)
                              .map(s => ({k:s, l:STATUS_STYLE[s].label,
                                          t:STATUS_STYLE[s].title, n:caseCounts[s]}))).map(p => {
                  const a = p.k === null ? statF.length === 0 : statF.includes(p.k);
                  return (<button key={p.l} title={p.t}
                    onClick={() => p.k === null ? setStatF([]) : setStatF(pr => pr.includes(p.k) ? pr.filter(x => x !== p.k) : [...pr, p.k])}
                    style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px", borderRadius:6, cursor:"pointer",
                      border:`1px solid ${a ? "#3b82f6" : "var(--border)"}`, background: a ? "#eff6ff" : "var(--white)",
                      color: a ? "#1d4ed8" : "var(--text-secondary)", fontSize:11, fontWeight: a ? 600 : 400, fontFamily:"var(--font-mono)" }}>
                    {p.k && <span style={{ width:6, height:6, borderRadius:"50%", background:STATUS_STYLE[p.k]?.dot }}/>}
                    {p.l} <span style={{ fontSize:9.5, opacity:0.6, fontWeight:700 }}>{p.n}</span>
                  </button>);
                })}
                <div style={{ width:1, height:22, background:"var(--border)", margin:"0 2px", alignSelf:"center" }}/>
              </>
            )}
            <button onClick={() => setAlertF(p => p.includes("ALERTS") ? [] : ["ALERTS"])} style={{
              display:"flex", alignItems:"center", gap:4, padding:"5px 10px", borderRadius:6, cursor:"pointer",
              border:`1px solid ${alertF.includes("ALERTS") ? "#fecaca" : "var(--border)"}`,
              background: alertF.includes("ALERTS") ? "#fef2f2" : "var(--white)",
              color: alertF.includes("ALERTS") ? "#dc2626" : "var(--text-secondary)",
              fontSize:11, fontWeight: alertF.includes("ALERTS") ? 600 : 400, fontFamily:"var(--font-mono)" }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              Alerts {caseCounts.alerts > 0 && <span style={{ fontSize:9.5, fontWeight:700, opacity:0.7 }}>{caseCounts.alerts}</span>}
            </button>
            {/* Cases where a technologist flagged a check for a reviewer. */}
            <button onClick={() => setFlagF(p => p.includes("FLAGGED") ? [] : ["FLAGGED"])} style={{
              display:"flex", alignItems:"center", gap:4, padding:"5px 10px", borderRadius:6, cursor:"pointer",
              border:`1px solid ${flagF.includes("FLAGGED") ? "#fde68a" : "var(--border)"}`,
              background: flagF.includes("FLAGGED") ? "#fffbeb" : "var(--white)",
              color: flagF.includes("FLAGGED") ? "#b45309" : "var(--text-secondary)",
              fontSize:11, fontWeight: flagF.includes("FLAGGED") ? 600 : 400, fontFamily:"var(--font-mono)" }}>
              <span style={{ fontSize:12, lineHeight:1 }}>&#9873;</span>
              Flagged {caseCounts.flagged > 0 && <span style={{ fontSize:9.5, fontWeight:700, opacity:0.7 }}>{caseCounts.flagged}</span>}
            </button>
            {/* The waveform reviewer's queue: cases picked for review that are
                not signed off yet, including carry-over from earlier months.
                The month's full list, finished ones included, is on the
                waveform-selection endpoint. */}
            {!isTech && !isIP && (
              <button onClick={() => setWaveF(p => p.includes("SELECTED") ? [] : ["SELECTED"])} style={{
                display:"flex", alignItems:"center", gap:4, padding:"5px 10px", borderRadius:6, cursor:"pointer",
                border:`1px solid ${waveF.includes("SELECTED") ? "#ccfbf1" : "var(--border)"}`,
                background: waveF.includes("SELECTED") ? "#f0fdfa" : "var(--white)",
                color: waveF.includes("SELECTED") ? "#0f766e" : "var(--text-secondary)",
                fontSize:11, fontWeight: waveF.includes("SELECTED") ? 600 : 400, fontFamily:"var(--font-mono)" }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M2 12h4l3-9 4 18 3-9h6"/>
                </svg>
                Waveform review {caseCounts.waveform > 0 && <span style={{ fontSize:9.5, fontWeight:700, opacity:0.7 }}>{caseCounts.waveform}</span>}
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Audit Type toggle + dropdown filters */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom: hasF ? 6 : 12, flexWrap:"wrap" }}>
          <div style={{ width:1, height:20, background:"var(--border)", margin:"0 2px" }}/>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5"><path d="M2 3h12M4 8h8M6 13h4" strokeLinecap="round"/></svg>
          <FilterDropdown label="Procedure" selected={procF} options={procs} onChange={setProcF}/>
          {facs.length > 0 && <FilterDropdown label="Facility" selected={facF} options={facs} onChange={setFacF}/>}
          {surgs.length > 0 && <FilterDropdown label="Surgeon" selected={surgF} options={surgs} onChange={setSurgF}/>}
          {techs.length > 0 && <FilterDropdown label="Technologist" selected={techF} options={techs} onChange={setTechF}/>}
          {!isTech && !isIP && reviewers.length > 0 && <FilterDropdown label="Reviewer" selected={revF} options={reviewers} onChange={setRevF}/>}
          {!isTech && !isIP && (
            <FilterDropdown label="Progress" selected={trackF} options={TRACK_OPTIONS.map(o => o.label)} onChange={setTrackF}/>
          )}
          <div style={{ width:1, height:20, background:"var(--border)", margin:"0 1px" }}/>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>SVC</span>
            <select value={quarter} onChange={e => applyQuarter(e.target.value)}
              style={{ width:96, fontSize:10.5, fontFamily:"var(--font-mono)", padding:"4px 6px", border:"1px solid var(--border)", borderRadius:5, color:"var(--text-secondary)", background:"var(--white)", outline:"none", cursor:"pointer" }}>
              <option value="">Quarter…</option>
              {quarterOpts.map(q => <option key={q} value={q}>{q}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={e => { setQuarter(""); setDateFrom(e.target.value); }}
              style={{ width:120, fontSize:10.5, fontFamily:"var(--font-mono)", padding:"4px 6px", border:"1px solid var(--border)", borderRadius:5, color:"var(--text-secondary)", background:"var(--white)", outline:"none" }}/>
            <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>TO</span>
            <input type="date" value={dateTo} onChange={e => { setQuarter(""); setDateTo(e.target.value); }}
              style={{ width:120, fontSize:10.5, fontFamily:"var(--font-mono)", padding:"4px 6px", border:"1px solid var(--border)", borderRadius:5, color:"var(--text-secondary)", background:"var(--white)", outline:"none" }}/>
          </div>
          {hasF && <button onClick={clearAll} style={{ fontSize:10.5, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", background:"none", border:"none", cursor:"pointer", textDecoration:"underline" }}>Clear all</button>}
        </div>

        {/* Active chips */}
        {hasF && (
          <div style={{ display:"flex", gap:4, marginBottom:10, flexWrap:"wrap", alignItems:"center" }}>
            {!isTech && statF.map(s => <Chip key={s} label="Status" value={s} onRemove={() => setStatF(statF.filter(x => x !== s))}/>)}
            {alertF.map(a => <Chip key={a} label="Alert" value="Has Alerts" onRemove={() => setAlertF([])}/>)}
            {flagF.map(f => <Chip key={f} label="Flag" value="Tech flagged" onRemove={() => setFlagF([])}/>)}
            {!isTech && !isIP && waveF.map(w => <Chip key={w} label="Waveform" value="Awaiting review" onRemove={() => setWaveF([])}/>)}
            {procF.map(v => <Chip key={v} label="Procedure" value={v} onRemove={() => setProcF(procF.filter(x => x !== v))}/>)}
            {facF.map(v => <Chip key={v} label="Facility" value={v} onRemove={() => setFacF(facF.filter(x => x !== v))}/>)}
            {surgF.map(v => <Chip key={v} label="Surgeon" value={v} onRemove={() => setSurgF(surgF.filter(x => x !== v))}/>)}
            {techF.map(v => <Chip key={v} label="Tech" value={v} onRemove={() => setTechF(techF.filter(x => x !== v))}/>)}
            {!isTech && !isIP && revF.map(v => <Chip key={v} label="Reviewer" value={v} onRemove={() => setRevF(revF.filter(x => x !== v))}/>)}
            {!isTech && !isIP && trackF.map(v => <Chip key={v} label="Progress" value={v} onRemove={() => setTrackF(trackF.filter(x => x !== v))}/>)}
            {quarter && <Chip label="Quarter" value={quarter} onRemove={() => applyQuarter("")}/>}
            {!quarter && (dateFrom||dateTo) && <Chip label="Service Date" value={`${dateFrom||"..."} → ${dateTo||"..."}`} onRemove={() => { setDateFrom(""); setDateTo(""); }}/>}
            <span style={{ fontSize:10.5, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)", marginLeft:3 }}>{filteredGroups.length} of {caseCounts.total}</span>
          </div>
        )}

        {/* Table */}
        <div className="card">
          <div className="table-wrap">
            {loading ? (
              <div style={{ padding:32 }}>{[0,1,2,3,4].map(i => <div key={i} style={{ display:"flex", gap:12, marginBottom:14, alignItems:"center" }}><div className="skeleton" style={{ height:12, width:16 }}/><div className="skeleton" style={{ height:12, flex:2 }}/><div className="skeleton" style={{ height:12, width:80 }}/><div className="skeleton" style={{ height:12, width:40 }}/><div className="skeleton" style={{ height:12, width:70 }}/></div>)}</div>
            ) : filteredGroups.length === 0 ? (
              <div style={{ padding:48, textAlign:"center", color:"var(--text-tertiary)" }}>
                <div style={{ fontSize:14, marginBottom:4 }}>No documents found</div>
                <div style={{ fontSize:12 }}>{hasF ? "Try adjusting your filters" : "Upload a document to get started"}</div>
              </div>
            ) : (
              <MSOStatusProvider caseIds={filteredGroups.map(g => g.pid)}>
              <table className="table-fixed">
                <thead><tr>
                  <th style={{ width:32, textAlign:"center", padding:"8px 4px" }}><Checkbox checked={allSel} indeterminate={someSel && !allSel} onChange={togAll}/></th>
                  <th onClick={() => toggleSort("filename")} style={{ cursor:"pointer", width:200 }}>Document <Arr c="filename"/></th>
                  <th onClick={() => toggleSort("serviceDate")} style={{ cursor:"pointer", width:90 }}>Service <Arr c="serviceDate"/></th>
                  <th style={{ width:150 }}>Tech</th>
                  <th style={{ width:150 }}>Reader</th>
                  <th onClick={() => toggleSort("techFlagOpen")} style={{ cursor:"pointer", width:62, textAlign:"center" }}
                    title="Checks flagged by the technologist for a reviewer">Flags <Arr c="techFlagOpen"/></th>
                  <th style={{ width:85, textAlign:"center" }}>Status</th>
                  {!isIP && <th style={{ width:64, textAlign:"center" }} title="Technologist audit score and sign-off">Doc</th>}
                  {!isIP && <th style={{ width:64, textAlign:"center" }} title="Waveform sign-off">Wave</th>}
                  {!isTech && <th style={{ width:64, textAlign:"center" }} title="Interpreting physician audit score and sign-off">IP</th>}
                  {!isTech && !isIP && <th style={{ width:110, textAlign:"center" }} title="Manager sign-off score">MSO</th>}
                  {!isTech && !isIP && <th style={{ width:56, textAlign:"center" }} title="IDR narrative drafted / reviewed">IDR</th>}
                  <th onClick={() => toggleSort("procedureType")} style={{ cursor:"pointer", width:170 }}>Procedure <Arr c="procedureType"/></th>
                  {!isTech && !isIP && <th onClick={() => toggleSort("reviewedBy")} style={{ cursor:"pointer", width:170 }}>Reviewer <Arr c="reviewedBy"/></th>}
                  <th style={{ width:60, textAlign:"center" }}>Alerts</th>
                  <th style={{ width:76, textAlign:"center" }}>QA</th>
                </tr></thead>
                <tbody>{filteredGroups.map(group => {
                  // Single-file group → flat row
                  if (group.docs.length === 1 && !isSourceFile(group.docs[0])) {
                    const doc = group.docs[0];
                    const isSel = sel.has(doc.id);
                    const adTech = getAuditScoreData(doc, "Technologist");
                    const adIp   = getAuditScoreData(doc, "IP");
                    return (
                      <tr key={doc.id} onClick={() => nav(`/documents/${encodeURIComponent(doc.id)}`)}
                      style={{ cursor:"pointer", background: isSel ? "#eff6ff" : undefined, transition:"background 0.1s" }}>
                        <td style={{ textAlign:"center", padding:"8px 4px" }}><Checkbox checked={isSel} onChange={() => togSel(doc.id)}/></td>
                        <td style={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight:500, fontSize:12.5 }}>
                          {doc.filename}
                          {isSourceFile(doc) && <> <SourceTypeBadge sourceType={doc.sourceType}/></>}
                        </td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{fmtSvc(doc.serviceDate)}</td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)", width:150 }}><TechCell doc={doc} /></td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)", width:150 }}>{doc.readerName || "—"}</td>
                        <td style={{ textAlign:"center" }}><FlagIndicator count={doc.techFlagOpen || 0} total={doc.techFlagTotal || 0}/></td>
                        <td style={{ textAlign:"center" }}><StatusBadge status={doc._st}/></td>
                        {!isIP && <td style={{ textAlign:"center" }}><TrackScore score={adTech.score} done={doc.techReviewed}/></td>}
                        {!isIP && <td style={{ textAlign:"center" }}><TrackScore score={doc.waveformScore} done={doc.waveformSigned}/></td>}
                        {!isTech && <td style={{ textAlign:"center" }}><TrackScore score={adIp.score} done={doc.ipReviewed}/></td>}
                        {!isTech && !isIP && (
                          <td style={{ textAlign:"center" }} onClick={e => e.stopPropagation()}>
                            <MSOStatusCell caseId={doc.patientId || doc.id} onOpen={() => nav(`/documents/${encodeURIComponent(doc.id)}?tab=mso`)}/>
                          </td>
                        )}
                        {!isTech && !isIP && <td style={{ textAlign:"center" }}><IdrCell status={doc.idrStatus} hasNarrative={doc.idrHasNarrative}/></td>}
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{doc.procedureType || "—"}</td>
                        {!isTech && !isIP && <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{doc.reviewedBy || "—"}</td>}
                        <td style={{ textAlign:"center" }}><AlertIndicator status={doc.alertStatus} count={doc.alertCount || 0}/></td>
                        <td></td>
                      </tr>
                    );
                  }

                  // Multi-file group → expandable
                  const isExpanded = expandedPids.has(group.pid);
                  const groupDocs = group.docs;
                  const pqDoc = group.pidQaDoc;
                  const hasPidQa = !!pqDoc;
                  const isRunningQa = pidQaRunning.has(group.pid);

                  const groupSelId = getGroupSelId(group);
                  const isGroupSel = sel.has(groupSelId);

                  const gTech = hasPidQa ? getAuditScoreData(pqDoc, "Technologist") : { score: null };
                  const gIp   = hasPidQa ? getAuditScoreData(pqDoc, "IP")           : { score: null };
                  const gAlerts = hasPidQa ? (pqDoc.alertCount || 0) : groupDocs.reduce((n, d) => n + (d.alertCount || 0), 0);
                  const gAlertStatus = hasPidQa
                    ? (pqDoc.alertStatus || "NO_ALERT")
                    : groupDocs.some(d => d.alertStatus === "ALERT" || d.alertStatus === "MANUAL_ALERT") ? "ALERT" : "NO_ALERT";
                  const gStatus = deriveStatus(hasPidQa ? pqDoc : (groupDocs[0] || {}),
                                              pidTrackers[group.pid], groupDocs.length);
                  const gProc = (hasPidQa ? pqDoc.procedureType : groupDocs[0]?.procedureType) || "—";
                  const gDate = groupDocs.reduce((latest, d) => (!latest || (d.uploadedAt && d.uploadedAt > latest)) ? d.uploadedAt : latest, "");
                  const gRep = hasPidQa ? pqDoc : groupDocs[0];
                  const gTracker = pidTrackers[group.pid];
                  const gLastQaAt = gTracker?.last_qa_at || null;
                  const gLastQaBy = gTracker?.last_qa_by || "";
                  const displayName = group.patientName || group.pid;
                  const pidDocId = hasPidQa ? pqDoc.id : null;

                  return (
                    <React.Fragment key={`pid-${group.pid}`}>
                      <tr onClick={() => hasPidQa ? nav(`/documents/${encodeURIComponent(pidDocId)}`) : togglePid(group.pid)}
                        style={{ cursor:"pointer", borderBottom:"1px solid var(--border)" }}>
                        <td style={{ textAlign:"center", padding:"8px 4px" }} onClick={e => e.stopPropagation()}>
                          <Checkbox checked={isGroupSel} onChange={() => togSel(groupSelId)}/>
                        </td>
                        <td style={{ fontWeight:600, fontSize:12.5 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round"
                              style={{ transition:"transform 0.15s", transform: isExpanded ? "rotate(90deg)" : "none", flexShrink:0 }}
                              onClick={e => { e.stopPropagation(); togglePid(group.pid); }}>
                              <path d="M6 4l4 4-4 4"/>
                            </svg>
                            <span>{displayName}</span>
                            <span style={{ fontSize:9.5, fontFamily:"var(--font-mono)", fontWeight:600, color:"#3b82f6", background:"#eff6ff",
                              padding:"1px 6px", borderRadius:10, border:"1px solid #bfdbfe" }}>
                              {groupDocs.length} files
                            </span>
                          </div>
                        </td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{fmtSvc(gRep?.serviceDate)}</td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)", width:150 }}><TechCell doc={gRep} /></td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)", width:150 }}>{gRep?.readerName || "—"}</td>
                        <td style={{ textAlign:"center" }}><FlagIndicator count={gRep?.techFlagOpen || 0} total={gRep?.techFlagTotal || 0}/></td>
                        <td style={{ textAlign:"center" }}>
                          {(() => {
                            const tracker = pidTrackers[group.pid];
                            // One status per case. This used to be a separate
                            // ladder that could disagree with the pill the case
                            // was counted under - a case with no PID_QA doc fell
                            // through to "Pending QA" whatever deriveStatus said.
                            const rcv = Object.values(tracker?.received_files || {}).filter(Boolean).length;
                            const exp = tracker?.expected_sources?.length;
                            const st = isRunningQa ? "Running" : gStatus;
                            const detail = (st === "Waiting for files" && exp)
                              ? `Waiting for the remaining source files — ${rcv} of ${exp} received`
                              : undefined;
                            return <StatusBadge status={st} title={detail}/>;
                          })()}
                        </td>
                        {!isIP && <td style={{ textAlign:"center" }}><TrackScore score={gTech.score} done={gRep?.techReviewed}   na={!hasPidQa}/></td>}
                        {!isIP && <td style={{ textAlign:"center" }}><TrackScore score={gRep?.waveformScore} done={gRep?.waveformSigned} na={!hasPidQa}/></td>}
                        {!isTech && <td style={{ textAlign:"center" }}><TrackScore score={gIp.score} done={gRep?.ipReviewed} na={!hasPidQa}/></td>}
                        {!isTech && !isIP && (
                          <td style={{ textAlign:"center" }} onClick={e => e.stopPropagation()}>
                            <MSOStatusCell caseId={group.pid} onOpen={() => pidDocId && nav(`/documents/${encodeURIComponent(pidDocId)}?tab=mso`)}/>
                          </td>
                        )}
                        {!isTech && !isIP && <td style={{ textAlign:"center" }}><IdrCell status={gRep?.idrStatus} hasNarrative={gRep?.idrHasNarrative}/></td>}
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{gProc}</td>
                        {!isTech && !isIP && <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{gRep?.reviewedBy || "—"}</td>}
                        <td style={{ textAlign:"center" }}><AlertIndicator status={gAlertStatus} count={gAlerts}/></td>
                        <td onClick={e => e.stopPropagation()} style={{ textAlign:"center" }}>
                          {hasPidQa
                            ? null
                            : (() => {
                                const tracker = pidTrackers[group.pid];
                                const ts = tracker?.status;
                                const pill = (label, tone) => (
                                  <span style={{
                                    display:"inline-flex", alignItems:"center", gap:4,
                                    fontSize:10, fontFamily:"var(--font-mono)",
                                    padding:"2px 8px", borderRadius:10, whiteSpace:"nowrap",
                                    background: tone === "busy" ? "#eef2ff" : "var(--surface-alt, #f1f5f9)",
                                    color: tone === "busy" ? "#4338ca" : "var(--text-tertiary)"
                                  }}>{label}</span>
                                );

                                if (ts === "QA_RUNNING" || isRunningQa) return pill("Running", "busy");

                                if (ts === "WAITING" && tracker?.expected_sources) {
                                  const rcv = Object.values(tracker?.received_files || {}).filter(Boolean).length;
                                  const exp = tracker.expected_sources.length;
                                  if (rcv < exp) return pill(`${rcv}/${exp} files`);
                                  return pill("Queued");
                                }
                                if (ts === "READY") return pill("Queued");

                                const allExtracted = groupDocs.every(d => d.alertStatus === "PENDING_PID_QA");
                                if (allExtracted)
                                  return <button className="btn btn-primary btn-sm"
                                    onClick={() => handleRunPidQa(group.pid, true)}
                                    disabled={isRunningQa}
                                    style={{ fontSize:10, fontFamily:"var(--font-mono)", padding:"3px 10px" }}>
                                    Run QA
                                  </button>;

                                return pill("Extract", "busy");
                              })()
                          }
                        </td>
                      </tr>
                      {isExpanded && groupDocs.map(doc => {
                        const isSel = sel.has(doc.id);
                        const isSrcTyped = isSourceFile(doc);
                        return (
                          <tr key={doc.id} onClick={() => nav(`/documents/${encodeURIComponent(doc.id)}`)}
                            style={{ cursor:"pointer", background: isSel ? "#eff6ff" : "var(--gray-50, #f9fafb)", transition:"background 0.1s" }}>
                            <td style={{ textAlign:"center", padding:"8px 4px" }}><Checkbox checked={isSel} onChange={() => togSel(doc.id)}/></td>
                            <td style={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:12, paddingLeft:28 }}>
                              <span style={{ color:"var(--text-tertiary)", marginRight:4 }}>└</span>
                              <span style={{ fontWeight:400 }}>{doc.filename}</span>
                              {doc.sourceType && <> <SourceTypeBadge sourceType={doc.sourceType}/></>}
                            </td>
                            <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{fmtSvc(doc.serviceDate)}</td>
                            <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}><TechCell doc={doc} /></td>
                            <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{doc.readerName || "—"}</td>
                            <td style={{ textAlign:"center" }}><FlagIndicator count={doc.techFlagOpen || 0} total={doc.techFlagTotal || 0}/></td>
                            <td style={{ textAlign:"center" }}>
                              {isSrcTyped
                                ? <span style={{ fontSize:9.5, fontFamily:"var(--font-mono)", color:"#0891b2", background:"#ecfeff", padding:"2px 6px", borderRadius:10, border:"1px solid #a5f3fc" }}>Extracted</span>
                                : <StatusBadge status={doc._st}/>}
                            </td>
                            {!isIP && <td style={{ textAlign:"center" }}><TrackScore score={getAuditScoreData(doc, "Technologist").score} done={doc.techReviewed}   na={isSrcTyped}/></td>}
                            {!isIP && <td style={{ textAlign:"center" }}><TrackScore score={doc.waveformScore} done={doc.waveformSigned} na={isSrcTyped}/></td>}
                            {!isTech && <td style={{ textAlign:"center" }}><TrackScore score={getAuditScoreData(doc, "IP").score} done={doc.ipReviewed} na={isSrcTyped}/></td>}
                            {!isTech && !isIP && <td style={{ textAlign:"center", color:"var(--text-tertiary)", fontSize:11 }}>—</td>}
                            {!isTech && !isIP && <td style={{ textAlign:"center" }}><IdrCell status={doc.idrStatus} hasNarrative={doc.idrHasNarrative}/></td>}
                            <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{isSrcTyped ? "—" : (doc.procedureType || "—")}</td>
                            {!isTech && !isIP && <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{isSrcTyped ? "—" : (doc.reviewedBy || "—")}</td>}
                            <td style={{ textAlign:"center" }}><AlertIndicator status={doc.alertStatus} count={doc.alertCount || 0}/></td>
                            <td></td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}</tbody>
              </table>
              </MSOStatusProvider>
            )}
          </div>
        </div>
      </div>

      <SelectionBar count={sel.size} onClear={() => setSel(new Set())} onAudit={() => nav(`/audit-files?ids=${[...sel].join(",")}`)}/>
      <style>{`@keyframes floatUp { from { opacity:0; transform:translateX(-50%) translateY(16px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
    </>
  );
}